import { Component, Injector, ViewChild, OnInit } from '@angular/core';
import { FormArray, UntypedFormControl, UntypedFormGroup, Validators, AbstractControl } from '@angular/forms';
import { finalize, map, shareReplay, take } from 'rxjs/operators';
import { BaseEntityComponent, BnLoadingService } from "@bndes/bndes-ux4/bndes-angular-utils";
import { BnModalMessageService } from "@bndes/bndes-ux4/bn-modal-message";
import { BnAlertsService } from "@bndes/bndes-ux4/bn-alerts";
import { Step, BnStepperComponent } from "@bndes/bndes-ux4/bn-stepper";
import { PermissaoService } from 'src/app/shared/permissao.service';
import { RemoverAlertasService } from 'src/app/shared/services/remover-alertas.service';
import { QuestionarioService } from './questionario.service';
import { QuestionarioAvaliacaoProjetoDTO, PerguntaEnergiaDTO, QuestionarioRespostaEnergiaDTO, DadosClienteDTO, MaquinasEquipamentosDTO } from '../model/questionario.model';
import { QuestionarioComplementarComponent } from './bloco-complementar/questionario-complementar.component';
import Utils from '../shared/fgi-utils';
import { ValidacaoRecaptchaComponent } from '../shared/recaptcha/validacao-recaptcha.component';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Estado } from '../model/estado.model';
import { Observable } from 'rxjs';
import { ROLE_GESTOR_AVALIADOR_FGE } from '../constantes/permissao.constants';
import { QuestionarioQualificacaoComponent } from './qualificacao/questionario-qualificacao.component';
import { QuestionarioRecomendacoesAoClienteComponent } from './bloco-recomendacoes-ao-cliente/questionario-recomendacoes-ao-cliente.component';

const idRespostaProjetoEnvolveApenasSubstituicao = 100;
const idRespostaProjetoEhMaisComplexo = 101;

@Component({
    selector: 'app-questionario-main',
    templateUrl: './questionario-main.component.html',
    styleUrls: ['./questionario-main.component.scss'],
    standalone: false
})
export class QuestionarioMainComponent extends BaseEntityComponent<QuestionarioAvaliacaoProjetoDTO> implements OnInit {

    public perguntasFaseAvaliacao: PerguntaEnergiaDTO[];
    public perguntasAvaliacaoCarregadas: boolean = false;
    public perguntasFaseComplementar: PerguntaEnergiaDTO[];
    public perguntasFaseEconomia: PerguntaEnergiaDTO[];
    public perguntasComplementarCarregadas: boolean = false;
    public perguntasEconomiaCarregadas: boolean = false;
    public perguntasQualificacao: PerguntaEnergiaDTO[];
    public perguntasQualificacaoCarregadas: boolean = false;
    public listaMaquinasEquipamentos: MaquinasEquipamentosDTO[];

    // somente quando se passa o id do questionário para recarregar
    public respostasCarregadas: boolean = false;
    public listaResposta: QuestionarioRespostaEnergiaDTO[] = null;

    public formGroup: UntypedFormGroup;

    public steps: Step[];
    public currentStep: number = 0;// só testesss
    public questionarioEnviado = false;
    public veioDoBotaoVoltar = false;

    usuarioAvaliadorFge$: Observable<Boolean>;

    @ViewChild(QuestionarioComplementarComponent)
    questionarioComplementar: QuestionarioComplementarComponent;

    @ViewChild(QuestionarioQualificacaoComponent)
    questionarioQualificacao: QuestionarioQualificacaoComponent;

    @ViewChild(QuestionarioRecomendacoesAoClienteComponent)
    questionarioRecomendacoesAoClienteComponent: QuestionarioRecomendacoesAoClienteComponent;

    @ViewChild(BnStepperComponent)
    stepper: BnStepperComponent;

    constructor(
            private modalService: BnModalMessageService,
            private ngbModal: NgbModal,
            private permissaoService: PermissaoService,
            protected injector: Injector,
            private questionarioService: QuestionarioService,
            private loadingService: BnLoadingService,
            private removerAlertasService: RemoverAlertasService,
            private alertsService: BnAlertsService) {

        super(questionarioService);
        this.config.rotaLista = "avisos/pesquisa";

        // TODO/FIXME: use para avancar o step (testes)
        // this.currentStep = 2;
    }

    ngOnInit() {

        const permissoes$ = this.permissaoService.getPermissoesUsuarioAutenticado().pipe(
            take(1),
            shareReplay(1),
            finalize(() => this.loadingService.liberaTela())
        );

        this.usuarioAvaliadorFge$ = permissoes$.pipe(
            map(prm => !!prm?.[ROLE_GESTOR_AVALIADOR_FGE])
        );

        this.criaFormularioDadosCliente();
        this.criaPassosPreenchimento();
        this.carregaPerguntas();
        this.trataParametrosQuestionario();

    }

    private criaPassosPreenchimento(): void {
        let stepIdentificacao: Step = new Step();
        stepIdentificacao.name = 'Identificação';

        let stepQualificacao: Step = new Step();
        stepQualificacao.name = 'Qualificação';
        stepQualificacao.disabled = true;

        let stepEconomia: Step = new Step();
        stepEconomia.name = 'Economia de energia';
        stepEconomia.disabled = true;

        let stepRecomendacoesAoCliente: Step = new Step();
        stepRecomendacoesAoCliente.name = 'Recomendações ao cliente';
        stepRecomendacoesAoCliente.disabled = true;

        let stepRevisao: Step = new Step();
        stepRevisao.name = 'Revisão';
        stepRevisao.disabled = true;

        let stepFinal: Step = new Step();
        stepFinal.name = 'Formulário Enviado';
        stepFinal.disabled = true;

        this.steps = [stepIdentificacao, stepQualificacao, stepEconomia, stepRecomendacoesAoCliente, stepRevisao, stepFinal];
    }

    private criaFormularioDadosCliente(): void {
        this.formGroup = this.formBuilder.group({
            cnpj: new UntypedFormControl('', Validators.required),
            isEmpresaTomadoraDiferente: new UntypedFormControl('', Validators.required),
            nomeRepresentante: new UntypedFormControl('', Validators.required),
            cpfRepresentante: new UntypedFormControl('', Validators.required),
            mailRepresentante: new UntypedFormControl('', Validators.required),
            telefoneRepresentante: new UntypedFormControl('', Validators.required),
            relacaoRepresentante: new UntypedFormControl('', Validators.required),
            descricaoProjeto: new UntypedFormControl('', [Validators.required, Validators.maxLength(1000)]),
            valorEstimadoProjeto: new UntypedFormControl('', [Validators.required]),
            cnpjBeneficiario: new UntypedFormControl('', [Validators.required]),
            razaoSocialBeneficiario: new UntypedFormControl('', [Validators.required]),
            cepBeneficiario: new UntypedFormControl('', [Validators.required]),
            logradouroBeneficiario: new UntypedFormControl('', [Validators.required]),
            complementoBeneficiario: new UntypedFormControl('', [Validators.required]),
            responsavelBeneficiario: new UntypedFormControl('', [Validators.required]),
            emailresponsavelBeneficiario: new UntypedFormControl('', [Validators.required]),
        });

        this.formGroup.get('isEmpresaTomadoraDiferente').valueChanges.pipe().subscribe(novoValor => {
            if (novoValor) {
                this.formGroup.get('cnpjBeneficiario').enable();
                this.formGroup.get('razaoSocialBeneficiario').enable();
                this.formGroup.get('cepBeneficiario').enable();
                this.formGroup.get('logradouroBeneficiario').enable();
                this.formGroup.get('complementoBeneficiario').enable();
                this.formGroup.get('responsavelBeneficiario').enable();
                this.formGroup.get('emailresponsavelBeneficiario').enable();
            } else {
                this.formGroup.get('cnpjBeneficiario').disable();
                this.formGroup.get('razaoSocialBeneficiario').disable();
                this.formGroup.get('cepBeneficiario').disable();
                this.formGroup.get('logradouroBeneficiario').disable();
                this.formGroup.get('complementoBeneficiario').disable();
                this.formGroup.get('responsavelBeneficiario').disable();
                this.formGroup.get('emailresponsavelBeneficiario').disable();
            }

            this.formGroup.updateValueAndValidity();
        });
    }

    private carregaPerguntas(): void {
        this.questionarioService.getListaMaquinasEquipamentos().subscribe(response => {
            this.listaMaquinasEquipamentos = response;
        });

        this.questionarioService.getPerguntasFaseComplementar().subscribe(response => {
            this.perguntasFaseComplementar = response;
            this.perguntasComplementarCarregadas = true;
            this.perguntasQualificacao = response;
            this.perguntasQualificacaoCarregadas = true;
        });

        this.questionarioService.getPerguntasFaseEconomia().subscribe(response => {
            this.perguntasFaseEconomia = response;
            this.perguntasEconomiaCarregadas = true;
        });

        try {
            this.questionarioService.getListaMaquinasEquipamentos().subscribe(response => {
                this.listaMaquinasEquipamentos = response;
            });

        } catch (err) {
            console.log('erro ao carregar dados', err);
            this.listaMaquinasEquipamentos = null;
            this.alertsService.alertaErro("Erro", "Erro ao carregar os dados");
        } finally {
            console.log('finalizou carregamento');

        }

    }

    private trataParametrosQuestionario(): void {
        this.route.params.subscribe(params => {
            let urlOrigin = this.router.url;
            let cnpjParam = params['cnpj'];
            let id = params['id'];
            if (urlOrigin.indexOf('rascunho') >= 0) {
                this.carregaPorRascunho(id, cnpjParam);
            } else if (urlOrigin.indexOf('token') >= 0) {
                this.carregaPorToken(id, cnpjParam);
            } else {
                // novo questionário
                this.respostasCarregadas = true;
            }
        });
    }

    private carregaPorRascunho(ideRascunho: string, cnpjParam: string) {
        this.loadingService.bloqueiaTela();
        this.questionarioService.getQuestionarioAvaliacaoByRascunho(ideRascunho).subscribe(response => {
            this.loadingService.liberaTela();
            if (!response) {
                this.modalService.abrir('Erro', `O rascunho informado (${ideRascunho}) não foi encontrado.`)
                    .result
                    .then(() => {
                        this.model = null;
                        this.router.navigate(['/menu-questionario']);
                    });

            } else {
                this.model = response;
                console.log("carregado com sucesso o rascunho. Versao: " + this.model.versao)
                if (this.validaQuestionarioCarregadoPorRascunho(cnpjParam, this.model)) {
                    this.carregarQuestionarioParaFormulario(this.model);
                } else {
                    this.model = undefined;
                }

            }
        }, error => {
            this.loadingService.liberaTela();
        });
    }

    private carregaPorToken(token: string, cnpjParam: string) {
        this.loadingService.bloqueiaTela();
        this.questionarioService.getQuestionarioAvaliacaoByToken(token).subscribe(response => {
            this.loadingService.liberaTela();
            if (!response) {
                this.modalService.abrir('Erro', `O token informado (${token}) não foi encontrado.`)
                    .result
                    .then(() => {
                        this.model = null;
                        this.router.navigate(['/menu-questionario']);
                    });

            } else {
                this.model = response;
                if (this.validaQuestionarioCarregadoPorToken(cnpjParam, this.model)) {
                    this.carregarQuestionarioParaFormulario(this.model);
                } else {
                    this.model = undefined;
                }
            }
        }, error => {
            this.loadingService.liberaTela();
        });
    }

    private carregarQuestionarioParaFormulario(obj: QuestionarioAvaliacaoProjetoDTO): void {
        this.formGroup.patchValue({
            cnpj: obj.dadosCliente.cnpjCliente,
            isEmpresaTomadoraDiferente: obj.dadosCliente.cnpjBeneficiario != null ? true : false,
            nomeRepresentante: obj.dadosCliente.nomeRepresentante,
            cpfRepresentante: obj.dadosCliente.cpfRepresentante,
            mailRepresentante: obj.dadosCliente.emailRepresentante,
            telefoneRepresentante: obj.dadosCliente.telefoneRepresentante,
            relacaoRepresentante: obj.dadosCliente.relacaoRepresentante,
            descricaoProjeto: obj.descricaoProjeto,

            valorEstimadoProjeto: obj.dadosCliente.valorEstimadoProjeto,
            cnpjBeneficiario: obj.dadosCliente.cnpjBeneficiario,
            razaoSocialBeneficiario: obj.dadosCliente.razaoSocialBeneficiario,
            cepBeneficiario: obj.dadosCliente.cepBeneficiario,
            logradouroBeneficiario: obj.dadosCliente.logradouroBeneficiario,
            complementoBeneficiario: obj.dadosCliente.complementoBeneficiario,
            responsavelBeneficiario: obj.dadosCliente.responsavelBeneficiario,
            emailresponsavelBeneficiario: obj.dadosCliente.emailresponsavelBeneficiario,
        });
        this.listaResposta = obj.listaResposta;
        this.respostasCarregadas = true;
    }

    private validaQuestionarioCarregadoPorRascunho(cnpj: string, questionario: QuestionarioAvaliacaoProjetoDTO): boolean {
        if (questionario.dadosCliente.cnpjCliente != cnpj) {
            this.modalService.abrir('Erro', `O cnpj informado (${cnpj}) diverge do Tomador de Crédito utilizado para responder o questionário.`)
                .result
                .then(() => {
                    this.model = null;
                    this.router.navigate(['/menu-questionario']);
                });
            return false;
        }
        if (questionario.token && questionario.token != "") {
            this.modalService.abrir('Erro', `O rascunho informado está relacionado a um questionário que já foi submetido. Utilize o número do token para consultá-lo.`)
                .result
                .then(() => {
                    this.model = null;
                    this.router.navigate(['/menu-questionario']);
                });
            return false
        }

        return true;
    }

    private validaQuestionarioCarregadoPorToken(cnpj: string, questionario: QuestionarioAvaliacaoProjetoDTO): boolean {
        if (questionario.dadosCliente.cnpjCliente != cnpj) {
            this.modalService.abrir('Erro', `O cnpj informado (${cnpj}) diverge do Tomador de Crédito utilizado para responder o questionário.`)
                .result
                .then(() => {
                    this.model = null;
                    this.router.navigate(['/menu-questionario']);
                });
            return false;
        }
        return true;
    }

    private montaRespostaQuestionario(): QuestionarioRespostaEnergiaDTO[] {
        let listaResposta: QuestionarioRespostaEnergiaDTO[] = [];
        // listaResposta.push(...this.questionarioAvaliacao.retornaRespostasPreenchidas());
        listaResposta.push(...this.questionarioQualificacao.retornaRespostasPreenchidas());
        listaResposta.push(...this.questionarioComplementar.retornaRespostasPreenchidas());

        return listaResposta;
    }

    private montaDadosCliente(): DadosClienteDTO {
        let dadosCliente = new DadosClienteDTO();
        dadosCliente.cnpjCliente = this.formGroup.controls.cnpj.value;
        dadosCliente.cpfRepresentante = this.formGroup.controls.cpfRepresentante.value;
        dadosCliente.emailRepresentante = this.formGroup.controls.mailRepresentante.value;
        dadosCliente.telefoneRepresentante = this.formGroup.controls.telefoneRepresentante.value;
        dadosCliente.isEmpresaTomadoraDiferente = this.formGroup.controls.isEmpresaTomadoraDiferente.value;
        dadosCliente.nomeRepresentante = this.formGroup.controls.nomeRepresentante.value;
        dadosCliente.relacaoRepresentante = this.formGroup.controls.relacaoRepresentante.value;

        dadosCliente.valorEstimadoProjeto = this.formGroup.controls.valorEstimadoProjeto.value || null;
        dadosCliente.cnpjBeneficiario = this.formGroup.controls.cnpjBeneficiario.value || null;
        dadosCliente.razaoSocialBeneficiario = this.formGroup.controls.razaoSocialBeneficiario.value || null;
        dadosCliente.cepBeneficiario = this.formGroup.controls.cepBeneficiario.value || null;
        dadosCliente.logradouroBeneficiario = this.formGroup.controls.logradouroBeneficiario.value || null;
        dadosCliente.complementoBeneficiario = this.formGroup.controls.complementoBeneficiario.value || null;
        dadosCliente.responsavelBeneficiario = this.formGroup.controls.responsavelBeneficiario.value || null;
        dadosCliente.emailresponsavelBeneficiario = this.formGroup.controls.emailresponsavelBeneficiario.value || null;

        return dadosCliente;
    }

    private getDescricaoProjeto(): string {
        return this.formGroup.controls.descricaoProjeto.value;
    }

    private getPerguntaTecnica(): boolean {
        return this.questionarioComplementar.exibeQuestoes;
    }

    private getValidarMaquinasEquipamentos(): boolean {
        let perguntaTecnica = this.getPerguntaTecnica();
        return this.currentStep === 2 && perguntaTecnica === false; // validarMaquinasEquipamentos apenas na fase Economia
    }

    voltarParaLista() {
        this.removerAlertasService.removerTodos();
        super.voltarParaLista();
    }

    public avisarMotivoParaPassoEstarDesabilitado(index: number): void {
        if (this.currentStep == 0) {
            this.alertsService.alertaErro('Erro', 'Preencha primeiramente o passo de Qualificação.');
        } else {
            this.alertsService.alertaErro('Erro', 'Pendente de tramento (mensagem).');
        }

    }

    public isEmpresaTomadoraDiferente(): boolean {
        return this.formGroup.controls.isEmpresaTomadoraDiferente.value;
    }

    /** Ações de Botões */
    public continuar(): void {
        if (this.currentStep == 0) { //Identificação
            this.formGroup.markAllAsTouched();
            this.continuarParaQualificacao();
        } else if (this.currentStep == 1) { // Qualificação
            this.continuarParaEconomiaDeEnergia();
        } else if (this.currentStep == 2) { // Economia de energia
            this.continuarParaRecomendacoesAoCliente();
        } else if (this.currentStep == 3) { // Recomendacoes ao cliente
            this.continuarParaRevisao();
        } else {
            this.alertsService.alertaErro('Erro', 'Ação Inválida.');
            return;
        }
    }

    public voltar(): void {
        if (this.currentStep > 0) {
            this.steps[this.currentStep].disabled = true;
            this.steps[this.currentStep].done = false;
            this.steps[this.currentStep].visited = false;
            this.stepper.gotoPrevStep();
        }
    }

    public voltarRevisao(): void {
        this.questionarioEnviado = false;
        this.veioDoBotaoVoltar = true;
        this.currentStep = 4;
    }

    public saveRascunho() {
        if (!this.formGroup.valid) {
            this.alertsService.alertaErro('Erro', 'O rascunho somente será salvo após o correto preenchimento dos campos de identificação.');
            return;
        }

        this.solicitarCaptcha().then((tokenCaptcha) => {
            console.log("procedendo a invocacao do backend para salvar o rascunho... token: " + tokenCaptcha);
            this.loadingService.bloqueiaTela();
            this.questionarioService.salvaRascunho(this.montaQuestionarioParaBackend(tokenCaptcha))
                .subscribe(retorno => {
                    console.log("versao do retorno: " + retorno.versao)
                    this.model.versao = retorno.versao;
                    this.loadingService.liberaTela();
                    this.modalService.abrir('Rascunho armazenado com sucesso', `Guarde o número do rascunho para continuar o preenchimento ${retorno.ideRascunho}`)
                        .result
                        .then(() => {
                            this.router.navigate([`questionario-eficiencia-energetica/${this.model.dadosCliente.cnpjCliente}/rascunho/${retorno.ideRascunho}`]);
                        });
                }, error => {
                    this.loadingService.liberaTela();
                });
        });
    }

    public submeter() {
        this.solicitarCaptcha().then((tokenCaptcha) => {
            console.log("procedendo a invocacao do backend para submeter o questionario... token: " + tokenCaptcha);

            this.loadingService.bloqueiaTela();
            this.questionarioService.submeterQuestionario(this.montaQuestionarioParaBackend(tokenCaptcha))
                .subscribe(retorno => {
                    this.questionarioEnviado = true;
                    this.model = retorno;
                    this.loadingService.liberaTela();
                    this.steps[5].disabled = false;
                    this.steps[4].done = true;
                    this.stepper.gotoNextStep();
                }, error => {
                    this.loadingService.liberaTela();
                });
        });
    }

    public clonar(): void {
        let novoModelo = new QuestionarioAvaliacaoProjetoDTO();
        this.model.listaResposta = novoModelo.listaResposta;
        this.model = novoModelo;

        this.questionarioEnviado = false;

        this.currentStep = 0;
    }

    public imprimir(): void {
        window.print();
    }

    private formPerguntaTecnica(): AbstractControl<any, any> {
        return this.questionarioComplementar.getFormPerguntaTecnica();
    }

    private haMaquinasEquipamentosFiname(): boolean {
        return this.questionarioQualificacao.haEquipamentosFiname;
    }

    private haEquipamentosFinameSemQuantidadeZeroNegativo(): boolean {
        return this.questionarioQualificacao.haEquipamentosFinameSemQuantidadeZeroNegativo;
    }

    private continuarParaQualificacao(): void {
        // retirar durante a apresentação aos agentes
        if (!this.formGroup.valid) {
            this.alertsService.alertaErro('Erro', 'Existem erros no formulário de identificação.');
            return;
        }
        if (!Utils.validaMail(this.formGroup.controls.mailRepresentante.value)) {
            this.alertsService.alertaErro('Erro', 'Formato do e-mail informado inválido.');
            return;
        }

        this.steps[1].disabled = false;
        this.steps[0].done = true;
        this.stepper.gotoNextStep();
    }

    private continuarParaEconomiaDeEnergia(): void {
        if (this.haEquipamentosFinameSemQuantidadeZeroNegativo()) {
            this.alertsService.alertaErro('Erro', 'Existem itens na lista de máquinas e equipamentos sem quantidade, com valor zero ou negativo, o que não é permitido.');
            return;
        }

        this.loadingService.bloqueiaTela();
        this.questionarioService.validaQuestionario(this.montaQuestionarioParaBackend(''), true)
            .subscribe(retorno => {
                this.loadingService.liberaTela();
                this.steps[2].disabled = false;
                this.steps[1].done = true;
                this.stepper.gotoNextStep();
            }, error => {
                this.loadingService.liberaTela();
            });
    }

    private continuarParaRecomendacoesAoCliente(): void {
        const questComplementar = this.questionarioComplementar.formGroup.get('respostas') as FormArray;
        let perguntaTecnica = this.formPerguntaTecnica();

        perguntaTecnica.markAsTouched();

        if (perguntaTecnica.invalid) {
            this.alertsService.alertaErro('Erro', 'Existem erros no formulário de Economia de Energia.');
            return;
        }

        if (!this.haMaquinasEquipamentosFiname() && perguntaTecnica.value?.resposta === idRespostaProjetoEnvolveApenasSubstituicao) {
            this.alertsService.alertaErro('Erro', 'Para a opção selecionada, se faz obrigatório o preenchimento da questão 5 do formulário de Qualificação.');
            return;
        }

        const valido = this.questionarioComplementar.verificarIntegridadeDosBlocos();

        if (perguntaTecnica.value?.resposta === idRespostaProjetoEhMaisComplexo && !valido) {
            this.alertsService.alertaErro('Erro', 'Responda pelo menos um bloco de perguntas, como por exemplo (1.a, 1.b, 1.c).');
            return;

        } else {
            questComplementar.markAsUntouched();
        }

        this.loadingService.bloqueiaTela();
        this.questionarioService.validaQuestionario(this.montaQuestionarioParaBackend(''), false)
            .subscribe(retorno => {
                this.loadingService.liberaTela();
                this.steps[3].disabled = false;
                this.steps[2].done = true;
                this.stepper.gotoNextStep();
            }, error => {
                this.loadingService.liberaTela();
            });
    }

    private continuarParaRevisao(): void {
        this.questionarioRecomendacoesAoClienteComponent.formGroup.markAllAsTouched();

        if (this.questionarioRecomendacoesAoClienteComponent.formGroup.invalid) {
            this.alertsService.alertaErro('Erro', 'A seleção dos termos de aceite é obrigatória.');
            return null;
        }

        this.loadingService.bloqueiaTela();
        this.questionarioService.validaQuestionario(this.montaQuestionarioParaBackend(''), false)
            .subscribe(retorno => {
                this.loadingService.liberaTela();
                this.steps[4].disabled = false;
                this.steps[3].done = true;
                this.stepper.gotoNextStep();
            }, error => {
                this.loadingService.liberaTela();

            });
    }

    public getTodasPerguntasParaRevisao(): PerguntaEnergiaDTO[] {
        let todasPerguntas: PerguntaEnergiaDTO[] = [];
        todasPerguntas.push(...this.perguntasFaseComplementar);
        todasPerguntas.push(...this.perguntasFaseEconomia);
        return todasPerguntas;
    }

    public montaRespostasParaRevisao(): QuestionarioRespostaEnergiaDTO[] {
        if (this.isQuestionarioComToken()) {
            return this.model.listaResposta;
        } else {
            return this.montaRespostaQuestionario();
        }
    }

    public montaDadosClienteParaRevisao(): DadosClienteDTO {
        if (this.isQuestionarioComToken()) {
            return this.model.dadosCliente;
        } else {
            return this.montaDadosCliente();
        }
    }

    public getDescricaoProjetoParaRevisao(): string {
        if (this.isQuestionarioComToken()) {
            return this.model.descricaoProjeto;
        } else {
            return this.getDescricaoProjeto();
        }
    }

    private montaQuestionarioParaBackend(tokenCaptcha: string): QuestionarioAvaliacaoProjetoDTO {
        if (!this.model) {
            this.model = new QuestionarioAvaliacaoProjetoDTO();
        }
        this.model.recaptchaResponseToken = tokenCaptcha;
        this.model.dadosCliente = this.montaDadosCliente();
        this.model.listaResposta = this.montaRespostaQuestionario();
        this.model.descricaoProjeto = this.getDescricaoProjeto();
        this.model.projetoEhMaisComplexo = this.getPerguntaTecnica();
        this.model.validarMaquinasEquipamentos = this.getValidarMaquinasEquipamentos();
        return this.model;
    }

    /** Controle da Visibilidade */
    public isQuestionarioComToken() {
        return this.model && this.model.token && this.model.token.trim().length > 0;
    }

    public isDadosCarregados() {
        return this.perguntasComplementarCarregadas && this.respostasCarregadas;
    }

    public podeContinuar(): boolean {
        return !this.isQuestionarioComToken() && this.currentStep <= 3;
    }

    public podeConfirmar(): boolean {
        return !this.isQuestionarioComToken() && this.currentStep == 4;
    }

    public podeImprimir(): boolean {
        return ((this.currentStep == 3 || this.isQuestionarioComToken()) && !this.questionarioEnviado);
    }

    public podeClonar(): boolean {
        return this.isQuestionarioComToken() && !this.questionarioEnviado && !this.veioDoBotaoVoltar;
    }

    public podeVoltarRevisao() {
        return this.questionarioEnviado;
    }

    public podeSalvarRascunho(): boolean {
        if (this.isQuestionarioComToken()) {
            return false;
        }
        return true;
    }

    public podeVoltar() {
        return (!this.isQuestionarioComToken() && this.currentStep > 0);
    }

    public podeMostrarRevisao(): boolean {
        return this.isDadosCarregados && !this.questionarioEnviado && (this.currentStep == 4 || this.isQuestionarioComToken());
    }

    public podeMostrarQuestionarioEnviado() {
        return this.currentStep == 5;
    }

    public podeMostrarRecomendacoesAoCliente() {
        return !this.isQuestionarioComToken() && this.isDadosCarregados && this.currentStep == 3;
    }

    public podeMostrarPerguntasEconomia() {
        return !this.isQuestionarioComToken() && this.isDadosCarregados && this.currentStep == 2;
    }

    /* public podeMostrarPerguntasAvaliacao() {
         return !this.isQuestionarioComToken() && this.isDadosCarregados && this.currentStep == 1;
     }*/

    public podeMostrarPerguntasQualificacao() {
        return !this.isQuestionarioComToken() && this.isDadosCarregados && this.currentStep == 1;
    }

    public podeMostrarIdentificacao() {
        return !this.isQuestionarioComToken() && this.isDadosCarregados && this.currentStep == 0;
    }

    public isEmpresaTomadoraDiferenteFieldsInvalid(): boolean {
        return this.formGroup.controls.isEmpresaTomadoraDiferente.invalid &&
            this.formGroup.controls.isEmpresaTomadoraDiferente.touched;
    }

    public solicitarCaptcha(): Promise<any> {
        return this.ngbModal.open(ValidacaoRecaptchaComponent, {
            size: 'lg',
            backdrop: 'static'
        }).result;
    }

    public getEstado(): Estado {
        return this.model?.estado;
    }

    public getJustificativa(): string {
        return this.model?.textoJustificativa;
    }

    public getValorProjeto(): number {
        return this.model?.dadosCliente?.valorEstimadoProjeto;
    }

}
